import { useEffect, useState } from 'react'
import { useP2PStore } from '../../store/p2pStore'
import { Mic, Camera, AlertCircle, CheckCircle } from 'lucide-react'

interface PermissionState {
  requested: boolean
  audioGranted: boolean
  videoGranted: boolean
  error?: string
  shouldShowGuidance: boolean
}

export function ProactivePermissions() {
  const { mediaService } = useP2PStore()
  const [permissionState, setPermissionState] = useState<PermissionState>({
    requested: false,
    audioGranted: false,
    videoGranted: false,
    shouldShowGuidance: false
  })

  const [showPermissionDialog, setShowPermissionDialog] = useState(false)

  // Detect device type
  const isDesktop = !(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
  const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !!(window as any).MSStream === false

  useEffect(() => {
    console.log('🔍 PROACTIVE PERMISSIONS DEBUG:', {
      mediaService: !!mediaService,
      isDesktop,
      isIOSSafari,
      userAgent: navigator.userAgent.substring(0, 100),
      shouldRequest: !!(mediaService && (isDesktop || isIOSSafari))
    })

    if (!mediaService || (!isDesktop && !isIOSSafari)) {
      console.log('⚠️ ProactivePermissions: Skipping - not desktop/iOS or no mediaService')
      return
    }

    // Request permissions proactively for desktop and iOS Safari users
    const requestPermissions = async () => {
      try {
        console.log('🚀 ProactivePermissions: Requesting permissions on app load')

        const result = await mediaService.requestProactivePermissions()

        setPermissionState({
          requested: true,
          audioGranted: result.audioGranted,
          videoGranted: result.videoGranted,
          error: result.error,
          shouldShowGuidance: result.shouldShowManualGuidance || false
        })

        // Show guidance dialog if permissions failed
        if (result.shouldShowManualGuidance && result.error) {
          setShowPermissionDialog(true)
        }

        console.log('🚀 ProactivePermissions: Result:', result)

        // EMERGENCY DEBUG: Check actual browser permission state
        if ('permissions' in navigator) {
          try {
            const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName })
            const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName })
            console.log('🔍 BROWSER PERMISSION STATE:', {
              micState: micPermission.state,
              cameraState: cameraPermission.state
            })
          } catch (permError) {
            console.warn('⚠️ Permission query failed:', permError)
          }
        }
      } catch (error) {
        console.error('🚀 ProactivePermissions: Failed:', error)
        setPermissionState({
          requested: true,
          audioGranted: false,
          videoGranted: false,
          error: 'Permission request failed',
          shouldShowGuidance: true
        })
        setShowPermissionDialog(true)
      }
    }

    // Small delay to let the app initialize
    const timeoutId = setTimeout(requestPermissions, 1000)

    return () => clearTimeout(timeoutId)
  }, [mediaService, isDesktop, isIOSSafari])

  const handleRetryPermissions = async () => {
    if (!mediaService) return

    try {
      const result = await mediaService.forceDesktopPermissions()

      setPermissionState({
        requested: true,
        audioGranted: result.audioPermission,
        videoGranted: result.videoPermission,
        error: result.error,
        shouldShowGuidance: result.permissionFlow === 'guided'
      })

      if (result.audioPermission || result.videoPermission) {
        setShowPermissionDialog(false)
      }
    } catch (error) {
      console.error('Permission retry failed:', error)
    }
  }

  const getBrowserGuidance = () => {
    const isChrome = navigator.userAgent.includes('Chrome')
    const isFirefox = navigator.userAgent.includes('Firefox')
    const isEdge = navigator.userAgent.includes('Edg')
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')

    if (isIOSSafari) {
      return {
        icon: '📱',
        title: 'iOS Safari Permissions',
        steps: [
          'Open iOS Settings app',
          'Go to Safari → Privacy & Security',
          'Tap "Camera & Microphone"',
          'Allow access for this website',
          'Return to Safari and refresh the page'
        ]
      }
    }

    if (isChrome) {
      return {
        icon: '🌐',
        title: 'Chrome Permissions',
        steps: [
          'Look for the camera/microphone icon in the address bar',
          'Click the icon and select "Allow"',
          'Or click the lock icon → Permissions → Allow Camera & Microphone'
        ]
      }
    }

    if (isFirefox) {
      return {
        icon: '🦊',
        title: 'Firefox Permissions',
        steps: [
          'Look for the camera/microphone icon in the address bar',
          'Click the icon and select "Allow"',
          'Or click the shield icon → Permissions → Allow Camera & Microphone'
        ]
      }
    }

    if (isEdge) {
      return {
        icon: '🔷',
        title: 'Edge Permissions',
        steps: [
          'Look for the camera/microphone icon in the address bar',
          'Click the icon and select "Allow"',
          'Or click the lock icon → Permissions → Allow Camera & Microphone'
        ]
      }
    }

    if (isSafari) {
      return {
        icon: '🧭',
        title: 'Safari Permissions',
        steps: [
          'Click Safari → Settings for This Website',
          'Set Camera and Microphone to "Allow"',
          'Refresh the page'
        ]
      }
    }

    return {
      icon: '⚙️',
      title: 'Browser Permissions',
      steps: [
        'Look for a camera/microphone icon in your browser\'s address bar',
        'Click the icon and select "Allow"',
        'Refresh the page after granting permissions'
      ]
    }
  }

  // EMERGENCY DEBUG: Always log render decision
  console.log('🔍 PROACTIVE PERMISSIONS RENDER DECISION:', {
    isDesktopOrIOS: isDesktop || isIOSSafari,
    permissionsWorking: permissionState.audioGranted && permissionState.videoGranted,
    requested: permissionState.requested,
    shouldRender: (isDesktop || isIOSSafari) && !(permissionState.audioGranted && permissionState.videoGranted) && permissionState.requested
  })

  // Don't render anything if not desktop/iOS or if permissions are working
  if (!isDesktop && !isIOSSafari) return null
  if (permissionState.audioGranted && permissionState.videoGranted) return null
  if (!permissionState.requested) return null

  const guidance = getBrowserGuidance()

  return (
    <>
      {/* Permission Status Indicator (subtle, non-intrusive) */}
      {(permissionState.shouldShowGuidance || permissionState.error) && !showPermissionDialog && (
        <div className="fixed top-4 right-4 z-50 max-w-xs">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r-lg shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertCircle className="h-4 w-4 text-yellow-400" />
              </div>
              <div className="ml-2 flex-1">
                <p className="text-sm text-yellow-800 font-medium">
                  Media permissions needed
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Camera/microphone access required for calls
                </p>
                <button
                  onClick={() => setShowPermissionDialog(true)}
                  className="mt-2 text-xs text-yellow-800 hover:text-yellow-900 underline"
                >
                  Fix now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permission Guidance Dialog */}
      {showPermissionDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">{guidance.icon}</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {guidance.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Enable camera and microphone access
                  </p>
                </div>
              </div>
            </div>

            {/* Permission Status */}
            <div className="p-6 border-b border-gray-200">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Mic className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700">Microphone</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {permissionState.audioGranted ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`text-xs ${permissionState.audioGranted ? 'text-green-600' : 'text-red-600'}`}>
                      {permissionState.audioGranted ? 'Granted' : 'Denied'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Camera className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700">Camera</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {permissionState.videoGranted ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`text-xs ${permissionState.videoGranted ? 'text-green-600' : 'text-red-600'}`}>
                      {permissionState.videoGranted ? 'Granted' : 'Denied'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="p-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                How to enable permissions:
              </h4>
              <ol className="space-y-2 text-sm text-gray-700">
                {guidance.steps.map((step, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-800 rounded-full text-xs flex items-center justify-center font-medium">
                      {index + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>

              {permissionState.error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>Error:</strong> {permissionState.error}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-gray-200 flex space-x-3">
              <button
                onClick={handleRetryPermissions}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => setShowPermissionDialog(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}